import MultiSelectorTarget from '@repo/common/components/MultiSelector/MultiSelectorTarget';
import { transformOwnersToSelectableItems } from '@repo/common/components/MultiSelector/multiSelectorUtils';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { useExtendedUserList } from '../../api/hooks/user/useExtendedUserList';
import { extractOwnerAndGroupIds } from './ownerUtils';

interface OptimizedOwnerRenderProps<T> {
	ownerField?: string;
	ownerGroupField?: string;
	record: T;
	label?: string;
	hideOnEmpty?: boolean;
}

/**
 * Lightweight read-only component to display owners (users and groups) without selector functionality.
 * Used when user doesn't have permission to edit owners.
 * Uses the abstracted MultiSelectorTarget for consistent owner display across the app.
 */
export function OptimizedOwnerRender<
	T extends Record<string, unknown> & { id: string },
>({
	ownerField = 'owners',
	ownerGroupField = 'owners_groups',
	record,
	label = 'owners',
	hideOnEmpty = true,
}: OptimizedOwnerRenderProps<T>) {
	const { activeUsers, disabledUsers, serviceAccountsActive, userGroups } =
		useExtendedUserList();

	const { ownerIds, groupIds } = useMemo(
		() => extractOwnerAndGroupIds(record, ownerField, ownerGroupField),
		[record, ownerField, ownerGroupField]
	);

	const selectedOwners = useMemo(() => {
		// Combine all users
		const allUsers = [
			...(activeUsers ?? []),
			...(disabledUsers ?? []),
			...(serviceAccountsActive ?? []),
		];

		// Find matching users
		const matchedUsers = ownerIds
			.map((ownerId) => allUsers.find((user) => user.id === ownerId))
			.filter((user) => !isNil(user))
			.map((user) => ({
				id: user!.id,
				display_name: user!.display_name,
				first_name: user!.first_name,
				last_name: user!.last_name,
				profile_picture: user!.profile_picture,
			}));

		// Find matching groups
		const matchedGroups = groupIds
			.map((groupId) => userGroups?.find((group) => group.id === groupId))
			.filter((group) => !isNil(group))
			.map((group) => ({
				id: group!.id,
				name: group!.name,
				icon: group!.icon,
			}));

		return transformOwnersToSelectableItems(matchedUsers, matchedGroups);
	}, [
		ownerIds,
		groupIds,
		activeUsers,
		disabledUsers,
		serviceAccountsActive,
		userGroups,
	]);

	return (
		<MultiSelectorTarget
			selected={selectedOwners}
			placeholder={`No ${label}`}
			hideOnEmpty={hideOnEmpty}
			readOnly
			iconType="avatar"
			property="owner"
			isMenuItemBadge={false}
			variant="tertiary"
		/>
	);
}
